import equal from "fast-deep-equal";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Venue, VenuePhoto } from "sweet-global";
import StarIcon from "sweet-web/components/icons/StarIcon";
import OutlineButton from "sweet-web/components/universal/Button/OutlineButton";
import SelectInput from "sweet-web/components/universal/Input/SelectInput";
import SingleImageInput from "sweet-web/components/universal/Input/SingleImageInput";
import { ItemLabel } from "sweet-web/components/universal/Text";
import { colors } from "sweet-web/lib/styles";

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  margin: 10px 0;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 10px;
`;

const Right = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  margin-left: 10px;
`;

const PrimaryWrapper = styled.div`
  margin-left: 10px;
  transform: translateY(2px);
  transition: all 100ms cubic-bezier(0.21, 0.94, 0.64, 0.99);
  &:hover {
    transform: scale(1.04);
  }
`;

const emptyPhoto: VenuePhoto = {
  primary: false,
  tag: null, // id of space or service or null for general venue photo
  file: null
};

type Props = {
  photoId?: string;
  photoEntity?: VenuePhoto;
  onSave: (photo: VenuePhoto) => void;
  onRemove?: () => void;
  onCancel?: () => void;
  makePrimary: (photoId: string, photo: VenuePhoto) => Promise<void>;
  tags: Array<{
    name: string;
    title: string;
  }>;
};

const EditPhoto: React.FC<Props> = props => {
  const [photo, setPhoto] = useState(
    props.photoEntity ? props.photoEntity : emptyPhoto
  );

  useEffect(() => {
    if (props.photoEntity) setPhoto(props.photoEntity);
  }, [props.photoEntity]);

  useEffect(() => {
    if (props.photoId && !equal(props.photoEntity, photo)) props.onSave(photo);
  }, [photo]);

  const [starHover, setStarHover] = useState(false);

  return (
    <Wrapper>
      <SingleImageInput
        value={photo.file}
        onChange={file => {
          setPhoto(p => ({
            ...p,
            file
          }));
        }}
      />
      <Right>
        <Row>
          <SelectInput
            value={photo.tag}
            style={{ padding: 0 }}
            options={props.tags}
            onChange={value => {
              setPhoto(p => ({
                ...p,
                tag: value
              }));
            }}
          />
          {props.photoId && (
            <PrimaryWrapper
              onClick={() => {
                if (!photo.primary) props.makePrimary(props.photoId, photo);
              }}
              onMouseOver={() => setStarHover(true)}
              onMouseLeave={() => setStarHover(false)}
            >
              <StarIcon
                size={24}
                color={
                  photo.primary || starHover ? colors.primary : colors.gray
                }
                fill={photo.primary ? colors.primary : colors.white}
              />
            </PrimaryWrapper>
          )}
        </Row>
        {!props.photoId && (
          <Row>
            <OutlineButton
              title="Save"
              onClick={() => {
                props.onSave(photo);
              }}
            />
            <OutlineButton
              style={{ marginLeft: 10 }}
              title="Cancel"
              color="gray"
              onClick={() => {
                if (
                  window.confirm(
                    "Are you sure you want to cancel adding this photo?"
                  )
                ) {
                  props.onCancel();
                }
              }}
            />
          </Row>
        )}
        {props.photoId && (
          <OutlineButton
            title="Remove"
            color="gray"
            onClick={() => {
              if (
                window.confirm("Are you sure you want to remove this photo?")
              ) {
                props.onRemove();
              }
            }}
          />
        )}
      </Right>
    </Wrapper>
  );
};

export default EditPhoto;
